// The "hand this to ChatGPT/Claude" prompt offered in the folder-import help.
// Kept out of the JSX so the wording — the part that actually needs editing as
// the importer evolves — is one plain string in one place.

/**
 * Briefing an assistant can follow to emit an importable batch: the folder
 * shape `parseFolder` expects, the `post.yaml` keys each adapter reads, and the
 * rules that cost real money to get wrong (links in `x.text`, YouTube titles).
 */
export const AI_IMPORT_PROMPT = `I want you to prepare a batch of short-form videos for publishing with Broadside, a tool that posts one video to several social platforms at once.

Produce a folder structure I can import directly.

FOLDER RULES
1. Create one parent folder, containing one subfolder per post.
2. Each post subfolder contains exactly two things: the video file (.mp4, .mov or .webm) and a file named post.yaml
3. Keep each parent folder to about 10 posts. For more, make several parent folders (batch_1, batch_2, ...) and import them one at a time.

post.yaml FORMAT
Use YAML. Use |- for any multi-line text so no trailing blank line is added.

video: myclip.mp4            # the exact video filename in this same folder
schedule: 2026-08-01T18:00   # optional. Local time, format YYYY-MM-DDTHH:mm
visibility: public           # optional. public, unlisted or private
platforms:
  youtube:
    title: "One line, max 100 characters"
    description: |-
      Longer description.
      Put hashtags here.
  instagram:
    caption: |-
      Caption text.
      Put hashtags here.
  tiktok:
    caption: |-
      Caption text with hashtags.
  facebook:
    caption: |-
      Caption text.
  x:
    text: |-
      Max 270 characters.

RULES THAT MATTER
- Put hashtags inside the caption/description itself. There is no separate hashtag field.
- Do NOT put links in the x.text field. X charges roughly 13x more for posts containing a URL.
- youtube.title must be a single line, 100 characters or fewer.
- visibility: must be exactly public, unlisted or private. Set it to public unless I say otherwise — omitting it means I have to choose one for the whole batch at import time. It is honoured by YouTube (and by TikTok via its own options); Instagram, Facebook and X always publish publicly and ignore it.
- Give every post its own unique schedule: value if you want a set cadence. One post per day is recommended and performs better than posting in bursts.
- Only include the platform keys you actually want to post to. Keys for platforms that aren't connected are ignored safely.
- Write copy that suits each platform rather than pasting identical text everywhere.

MY CONTENT
[Describe your videos here — one line per video, with whatever caption ideas, links or notes you have. I'll generate the folders and post.yaml files from this.]`;

/**
 * Copy `text` with the async Clipboard API, resolving to `false` rather than
 * throwing when it is unavailable (insecure context) or rejected (permissions,
 * missing user gesture) — callers show the text instead of failing silently.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
